/** PDF-only presentation shared by downloaded reports and server-generated attachments. */
export interface PdfHierarchyRow {
  level: 'total' | 'portfolio' | 'subfolio' | 'security' | 'combined-security';
  label: string;
  depth: number;
}
type Colour = [number, number, number];
const ink: Colour = [23, 60, 53], white: Colour = [255, 255, 255];
const fills: Record<PdfHierarchyRow['level'], Colour> = {
  total: ink, portfolio: [216, 230, 223], subfolio: [238, 244, 240], security: white, 'combined-security': white,
};
export function pdfHierarchyStyle(row: PdfHierarchyRow, nameCell: boolean, verticalPadding: number) {
  return {
    fillColor: fills[row.level], textColor: row.level === 'total' ? white : ink,
    fontStyle: (['total', 'portfolio', 'subfolio'].includes(row.level) ? 'bold' : 'normal') as 'bold' | 'normal',
    lineColor: [153, 177, 164] as Colour,
    lineWidth: { top: row.level === 'portfolio' ? 0.3 : 0, right: 0, bottom: 0, left: 0 },
    cellPadding: { top: verticalPadding, bottom: verticalPadding, right: 3, left: nameCell ? 3 + 4.5 * row.depth : 3 },
  };
}

/** Only visible ancestors appear in the breadcrumb; a new group does not inherit an old sibling. */
export function pdfHierarchyParents(rows: PdfHierarchyRow[]): string[] {
  let ancestors: PdfHierarchyRow[] = [];
  return rows.map(row => {
    ancestors = ancestors.filter(parent => parent.depth < row.depth);
    const label = ancestors.map(parent => parent.label.replace(/\s+/g, ' ').trim()).join(' > ');
    if (row.level === 'portfolio' || row.level === 'subfolio') ancestors.push(row);
    return label;
  });
}

// Structural adapter keeps jsPDF and AutoTable out of the shared package's runtime dependencies.
interface PdfDocument {
  getFont(): { fontName: string; fontStyle: string }; getFontSize(): number; getTextColor(): string;
  setFont(name: string, style: string): unknown; setFontSize(size: number): unknown; setTextColor(colour: string): unknown;
  getTextWidth(text: string): number; text(text: string, x: number, y: number): unknown;
  internal: { pageSize: { getWidth(): number } };
}
interface CellHook {
  section: string; pageNumber: number; row: { index: number }; column: { index: number }; cell: { styles: object };
  doc: PdfDocument; table: { settings: { margin: { left: number; right: number; top: number } } };
}
export function pdfHierarchyHooks(rows: PdfHierarchyRow[], verticalPadding: number) {
  const parents = pdfHierarchyParents(rows), seenPages = new Set<number>();
  return {
    didParseCell(data: CellHook) {
      if (data.section === 'body' && rows[data.row.index]) Object.assign(data.cell.styles, pdfHierarchyStyle(rows[data.row.index], data.column.index === 0, verticalPadding));
    },
    willDrawCell(data: CellHook) {
      if (data.section !== 'body' || data.column.index !== 0 || seenPages.has(data.pageNumber)) return;
      seenPages.add(data.pageNumber);
      const parent = parents[data.row.index];
      if (data.pageNumber === 1 || !parent) return;
      const { doc } = data, { left, right, top } = data.table.settings.margin;
      const font = doc.getFont(), size = doc.getFontSize(), colour = doc.getTextColor();
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor('#53675f');
      const suffix = ' (continued)', available = doc.internal.pageSize.getWidth() - left - right;
      let label = parent;
      if (doc.getTextWidth(label + suffix) > available) {
        while (label && doc.getTextWidth(label + '...' + suffix) > available) label = label.slice(0, -1);
        label = label.trimEnd() + '...';
      }
      doc.text(label + suffix, left, top - 3);
      doc.setFont(font.fontName, font.fontStyle); doc.setFontSize(size); doc.setTextColor(colour);
    },
  };
}
